import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ArrowLeft, FilePdf, FileImage, LinkSimple } from "@phosphor-icons/react";
import { formatDateDisplay } from "@/components/ResourceSection";
import VaultLogo from "@/components/VaultLogo";

export default function PublicShare() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/share/public/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "this vault doesn't exist or is private." : "couldn't load"));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <p className="font-display text-3xl font-semibold tracking-tight mb-2">vault not found</p>
        <p className="text-[var(--brand-muted)] mb-6">{err}</p>
        <Link to="/" className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 hover:bg-[var(--brand-ink)]">go home</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-sm font-mono-d">opening vault…</div>;
  }

  const pi = data.personal_info || {};

  return (
    <div className="min-h-screen noise">
      {/* Header */}
      <header className="border-b-[1.5px] border-[var(--brand-ink)]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-brand">
            <VaultLogo size={24}/>
            <span className="font-display text-xl font-semibold tracking-tight">JobVault</span>
          </Link>
          <span className="label-tiny text-[var(--brand-muted)] font-mono-d">public share · read-only</span>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12" data-testid="public-share-page">
        {/* Hero */}
        <section className="border-b-[1.5px] border-[var(--brand-ink)] pb-10 mb-10">
          <p className="label-tiny text-[var(--brand-muted)] mb-3">/profile</p>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">{pi.full_name || data.name}</h1>
          {pi.headline && <p className="text-xl text-[var(--brand-muted)] mt-2">{pi.headline}</p>}
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            {pi.location && <span className="font-mono-d">📍 {pi.location}</span>}
            {pi.linkedin && <a href={pi.linkedin} target="_blank" rel="noopener noreferrer" className="underline flex items-center gap-1"><LinkSimple size={14}/> linkedin</a>}
            {pi.website && <a href={pi.website} target="_blank" rel="noopener noreferrer" className="underline flex items-center gap-1"><LinkSimple size={14}/> website</a>}
            {pi.portfolio && <a href={pi.portfolio} target="_blank" rel="noopener noreferrer" className="underline flex items-center gap-1"><LinkSimple size={14}/> portfolio</a>}
          </div>
          {pi.summary && <p className="mt-6 max-w-2xl leading-relaxed">{pi.summary}</p>}
        </section>

        {/* Work */}
        {data.work_experience?.length > 0 && (
          <Section label="work experience" testid="public-work">
            {data.work_experience.map((w) => (
              <Entry key={w.id || `${w.role}-${w.employer}`}
                primary={`${w.role} · ${w.employer}`}
                secondary={`${formatDateDisplay(w.start_date)} — ${w.current ? "now" : formatDateDisplay(w.end_date)}`}
                detail={w.responsibilities}
              />
            ))}
          </Section>
        )}

        {/* Education */}
        {data.education?.length > 0 && (
          <Section label="education" testid="public-edu">
            {data.education.map((e) => (
              <Entry key={e.id || `${e.qualification}-${e.provider}`}
                primary={`${e.qualification} · ${e.provider}`}
                secondary={`${formatDateDisplay(e.start_date)} — ${formatDateDisplay(e.end_date)}${e.grade ? ` · ${e.grade}` : ""}`}
              />
            ))}
          </Section>
        )}

        {/* Apprenticeships */}
        {data.apprenticeships?.length > 0 && (
          <Section label="apprenticeships" testid="public-appr">
            {data.apprenticeships.map((a) => (
              <Entry key={a.id}
                primary={`${a.role} @ ${a.employer}`}
                secondary={`${a.qualification} · ${a.level} · ${formatDateDisplay(a.start_date)} — ${formatDateDisplay(a.end_date)}`}
                detail={a.responsibilities}
              />
            ))}
          </Section>
        )}

        {/* Documents (only if owner enabled) */}
        {data.documents?.length > 0 && (
          <Section label="documents" testid="public-docs">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.documents.map((d) => {
                const Icon = d.content_type === "application/pdf" ? FilePdf : FileImage;
                return (
                  <div key={d.id} className="border-[1.5px] border-[var(--brand-ink)] rounded-md p-3 flex items-center gap-3 bg-white">
                    <Icon size={22} weight="duotone"/>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs label-tiny">{d.doc_type}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Footer CTA */}
        <div className="mt-16 card-hard rounded-md p-8 bg-[var(--pastel-yellow)] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <p className="label-tiny">/built with JobVault</p>
            <p className="font-display text-2xl font-semibold tracking-tight mt-1">make your own job vault.</p>
            <p className="text-sm text-[var(--brand-muted)] mt-1">lock it once. use it forever.</p>
          </div>
          <Link to="/register" data-testid="public-cta-register" className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-5 py-3 inline-flex items-center gap-2 hover:bg-[var(--brand-ink)]">
            start free <ArrowLeft size={16} weight="bold" className="rotate-180"/>
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ label, testid, children }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <p className="label-tiny mb-4">/{label}</p>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
function Entry({ primary, secondary, detail }) {
  return (
    <div className="border-l-[3px] border-[var(--brand-ink)] pl-4">
      <p className="font-medium">{primary}</p>
      <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-0.5">{secondary}</p>
      {detail && <p className="text-sm mt-2 leading-relaxed">{detail}</p>}
    </div>
  );
}
